import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { store } from 'redux/store';
import { useTranslate } from 'hooks/useTranslate';
import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import { updateDocument } from 'modules/documents/redux/documentActions';
import { ensureIncentivesMarketTermsAndConditionsTemplate } from 'modules/incentivesMarket/api/ensureIncentivesMarketTermsAndConditionsTemplate';

import { editIncentivesMarketTermsAndConditionsTemplateModalInitValue } from '../constants/editIncentivesMarketTermsAndConditionsTemplateModalInitValue';
import { EditIncentivesMarketTermsAndConditionsTemplateModalContextType } from '../types/EditIncentivesMarketTermsAndConditionsTemplateModalContextType';

export const EditIncentivesMarketTermsAndConditionsTemplateModalContext =
  createContext<EditIncentivesMarketTermsAndConditionsTemplateModalContextType>(
    editIncentivesMarketTermsAndConditionsTemplateModalInitValue
  );

const normalizeEditorModel = (editorModel: unknown): string => {
  if (editorModel == null) return '';
  if (typeof editorModel === 'string') return editorModel;
  return '';
};

export const EditIncentivesMarketTermsAndConditionsTemplateModalProvider: FC<{
  children: ReactNode;
  societyId: string;
}> = ({ children, societyId }) => {
  const { t } = useTranslate();

  const [documentId, setDocumentId] = useState<string | null>(null);
  const [termsText, setTermsText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [editorInstance, setEditorInstance] = useState<any>(null);

  const handleCloseModal = useCallback(() => {
    store.dispatch(setModal(null));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        setLoadError(false);
        const doc = await ensureIncentivesMarketTermsAndConditionsTemplate(
          societyId
        );
        if (cancelled || !doc?._id) {
          if (!cancelled && !doc?._id) setLoadError(true);
          return;
        }
        setDocumentId(doc._id);
        setTermsText(normalizeEditorModel(doc.editorModel));
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (societyId) load();

    return () => {
      cancelled = true;
    };
  }, [societyId]);

  const onEditorChange = useCallback(() => {
    if (editorInstance) {
      const content = editorInstance.getData();
      setTermsText(content);
    }
  }, [editorInstance]);

  const handleSave = useCallback(async () => {
    if (!documentId) {
      errorSwal(t('IncentivesMarketTermsTemplateSaveError'));
      return;
    }

    const currentContent = editorInstance
      ? editorInstance.getData()
      : termsText;

    try {
      setIsSaving(true);
      const updated = (await store.dispatch(
        updateDocument(
          {
            _id: documentId,
            editorModel: currentContent,
          },
          false
        )
      )) as { _id?: string } | undefined;
      if (updated?._id) {
        successSwal(t('IncentivesMarketTermsTemplateSaved'));
        handleCloseModal();
      } else {
        errorSwal(t('IncentivesMarketTermsTemplateSaveError'));
      }
    } catch {
      errorSwal(t('IncentivesMarketTermsTemplateSaveError'));
    } finally {
      setIsSaving(false);
    }
  }, [documentId, editorInstance, handleCloseModal, termsText, t]);

  const contextValue: EditIncentivesMarketTermsAndConditionsTemplateModalContextType =
    useMemo(
      () => ({
        data: {
          societyId,
          documentId,
          termsText,
          isLoading,
          isSaving,
          loadError,
          editorInstance,
        },
        actions: {
          handleCloseModal,
          setTermsText,
          setEditorInstance,
          onEditorChange,
          handleSave,
        },
      }),
      [
        societyId,
        documentId,
        termsText,
        isLoading,
        isSaving,
        loadError,
        editorInstance,
        handleCloseModal,
        onEditorChange,
        handleSave,
      ]
    );

  return (
    <EditIncentivesMarketTermsAndConditionsTemplateModalContext.Provider
      value={contextValue}
    >
      {children}
    </EditIncentivesMarketTermsAndConditionsTemplateModalContext.Provider>
  );
};

export const useEditIncentivesMarketTermsAndConditionsTemplateModal = () =>
  useContext(EditIncentivesMarketTermsAndConditionsTemplateModalContext);
